import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Search, TrendingUp, TrendingDown, BookOpen, ExternalLink, Plus, Trash2 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { REFERENCE_LIBRARY } from "./referenceLibrary";
import { Dialog as Dlg2, DialogContent as DlgC2, DialogHeader as DlgH2, DialogTitle as DlgT2, DialogFooter as DlgF2 } from "../../components/ui/dialog";
import { toast } from "sonner";
import { formatApiError } from "../../lib/api";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../../components/ui/select";
import { useAuth } from "../../context/AuthContext";

const SECTOR_COLORS = {
  banks: "bg-blue-50 text-blue-700 border-blue-200",
  it: "bg-indigo-50 text-indigo-700 border-indigo-200",
  pharma: "bg-emerald-50 text-emerald-700 border-emerald-200",
  auto: "bg-orange-50 text-orange-700 border-orange-200",
  fmcg: "bg-amber-50 text-amber-700 border-amber-200",
  energy: "bg-yellow-50 text-yellow-700 border-yellow-200",
  metals: "bg-slate-50 text-slate-700 border-slate-200",
  cement: "bg-stone-50 text-stone-700 border-stone-200",
  infra: "bg-cyan-50 text-cyan-700 border-cyan-200",
  telecom: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  realty: "bg-rose-50 text-rose-700 border-rose-200",
  financial_services: "bg-sky-50 text-sky-700 border-sky-200",
  power: "bg-lime-50 text-lime-700 border-lime-200",
  media: "bg-pink-50 text-pink-700 border-pink-200",
  chemicals: "bg-teal-50 text-teal-700 border-teal-200",
  textiles: "bg-purple-50 text-purple-700 border-purple-200",
  capital_goods: "bg-violet-50 text-violet-700 border-violet-200",
  consumer_durables: "bg-red-50 text-red-700 border-red-200",
  logistics: "bg-emerald-50 text-emerald-700 border-emerald-200",
  retail: "bg-pink-50 text-pink-700 border-pink-200",
  agri: "bg-green-50 text-green-700 border-green-200",
  others: "bg-gray-50 text-gray-700 border-gray-200",
};

export default function KipMarkets() {
  const { user } = useAuth();
  const [sectors, setSectors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [sector, setSector] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const canAdd = ["partner", "admin", "manager"].includes(user?.role);

  const reload = () => {
    const params = {};
    if (sector !== "all") params.sector = sector;
    if (q) params.q = q;
    params.limit = 600;
    api.get("/kip/markets/companies", { params }).then(({ data }) => setCompanies(data));
  };

  useEffect(() => {
    api.get("/kip/markets/sectors").then(({ data }) => setSectors(data));
  }, []);
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [sector, q]);

  const removeCustom = async (sym) => {
    if (!window.confirm("Remove this custom company?")) return;
    try { await api.delete(`/kip/markets/companies/${sym}`); toast.success("Removed"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="kip-markets-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow">Knowledge Intelligence</div>
          <h1 className="font-sans text-3xl">Markets & Listed Companies</h1>
          <p className="text-sm text-slate-600 mt-1">NSE 500 + your custom companies. Live price &amp; ratios via yfinance. {companies.length} loaded.</p>
        </div>
        {canAdd && <Button onClick={() => setShowAdd(true)} data-testid="add-company-btn"><Plus className="h-4 w-4 mr-1"/>Add by Scrip Code</Button>}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="sector-tabs">
        <button onClick={() => setSector("all")}
          className={`px-3 py-1.5 rounded-full text-xs border ${sector === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
          All Sectors
        </button>
        {sectors.map((s) => (
          <button key={s.id} onClick={() => setSector(s.id)}
            className={`px-3 py-1.5 rounded-full text-xs border ${sector === s.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
            data-testid={`sector-${s.id}`}>
            <span className="mr-1">{s.icon}</span>{s.name}
          </button>
        ))}
      </div>

      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
        <Input placeholder="Search by name or symbol…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9"/>
      </div>

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm" data-testid="companies-table">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-3">Symbol</th>
              <th className="text-left p-3">Company</th>
              <th className="text-left p-3">Sector</th>
              <th className="text-right p-3">Revenue (Cr)</th>
              <th className="text-right p-3">EBITDA %</th>
              <th className="text-right p-3">PE</th>
              <th className="text-right p-3">ROE %</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {companies.map((c) => (
              <tr key={c.symbol} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(c)} data-testid={`co-${c.symbol}`}>
                <td className="p-3 font-mono text-xs">
                  {c.symbol}
                  {c.custom && <Badge className="ml-1 bg-violet-100 text-violet-700 text-[9px]">custom</Badge>}
                </td>
                <td className="p-3 font-medium text-slate-800">{c.name}</td>
                <td className="p-3"><Badge variant="outline" className={`capitalize ${SECTOR_COLORS[c.sector] || ""}`}>{c.sector.replace("_", " ")}</Badge></td>
                <td className="p-3 text-right font-mono">{c.revenue_cr?.toLocaleString("en-IN") || "—"}</td>
                <td className="p-3 text-right font-mono">{c.ebitda_margin ?? "—"}</td>
                <td className="p-3 text-right font-mono">{c.pe ?? "—"}</td>
                <td className="p-3 text-right font-mono">{c.roe_pct ?? "—"}</td>
                <td className="p-3 text-right">
                  {c.custom && canAdd && (
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); removeCustom(c.symbol); }} data-testid={`del-${c.symbol}`}><Trash2 className="h-3 w-3"/></Button>
                  )}
                </td>
              </tr>
            ))}
            {!companies.length && <tr><td colSpan="8" className="text-center text-slate-400 p-8">No companies match this filter</td></tr>}
          </tbody>
        </table>
      </Card>

      {selected && <CompanyDetail symbol={selected.symbol} onClose={() => setSelected(null)}/>}
      {showAdd && <AddCompanyDialog onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); reload(); }} sectors={sectors}/>}
    </div>
  );
}

function AddCompanyDialog({ onClose, onSaved, sectors }) {
  const [symbol, setSymbol] = useState("");
  const [exchange, setExchange] = useState("NSE");
  const [manualSector, setManualSector] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!symbol.trim()) { toast.error("Enter NSE symbol or BSE code"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/kip/markets/companies/add", {
        symbol: symbol.trim().toUpperCase(), exchange, sector: manualSector || undefined,
      });
      toast.success(`Added ${data.name} (${data.sector})`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dlg2 open onOpenChange={(o) => !o && onClose()}>
      <DlgC2 data-testid="add-company-dialog" className="max-w-md">
        <DlgH2><DlgT2>Add Company by Scrip Code</DlgT2></DlgH2>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs text-slate-500">Exchange</label>
            <Select value={exchange} onValueChange={setExchange}>
              <SelectTrigger data-testid="add-co-exchange"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="NSE">NSE (use trading symbol e.g. TCS, INFY)</SelectItem>
                <SelectItem value="BSE">BSE (use scrip code e.g. 500325)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Symbol / Code</label>
            <Input value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder={exchange === "NSE" ? "e.g. TCS" : "e.g. 500325"} data-testid="add-co-symbol"/>
          </div>
          <div>
            <label className="text-xs text-slate-500">Manual Sector (optional — used if auto-detection fails)</label>
            <Select value={manualSector || "_none"} onValueChange={(v) => setManualSector(v === "_none" ? "" : v)}>
              <SelectTrigger data-testid="add-co-sector"><SelectValue placeholder="(auto-detect)"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">(auto-detect)</SelectItem>
                {sectors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DlgF2>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="add-co-save">{saving ? "Fetching…" : "Add Company"}</Button>
        </DlgF2>
      </DlgC2>
    </Dlg2>
  );
}

function CompanyDetail({ symbol, onClose }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/kip/markets/company/${symbol}`)
      .then(({ data }) => setD(data))
      .finally(() => setLoading(false));
  }, [symbol]);

  const fmt = (v) => v == null ? "—" : (typeof v === "number" ? v.toLocaleString("en-IN") : v);
  const refs = REFERENCE_LIBRARY.filter((r) => ["sebi", "valuation"].includes(r.section)).slice(0, 4);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="company-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Badge className={SECTOR_COLORS[d?.sector] || ""}>{d?.sector?.replace("_", " ") || ""}</Badge>
            <span>{d?.name || symbol}</span>
            <span className="font-mono text-xs text-slate-500">{symbol}</span>
          </DialogTitle>
        </DialogHeader>
        {loading ? <div className="text-center text-slate-400 p-8">Loading live data…</div> : d && (
          <div className="space-y-6">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              <KpiBox label="Live Price" value={d.live_price ? `₹${fmt(d.live_price.toFixed?.(2) || d.live_price)}` : "Unavailable"} accent="emerald"/>
              <KpiBox label="Market Cap" value={d.market_cap ? `₹${(d.market_cap / 1e7).toFixed(0)} Cr` : `₹${fmt(d.market_cap_cr)} Cr`} accent="blue"/>
              <KpiBox label="P/E" value={fmt(d.pe?.toFixed?.(1) || d.pe)} accent="violet"/>
              <KpiBox label="P/B" value={fmt(d.pb?.toFixed?.(2) || d.pb)} accent="rose"/>
              <KpiBox label="ROE %" value={d.roe ? (d.roe * 100).toFixed(1) : fmt(d.roe_pct)} accent="amber"/>
              <KpiBox label="Div Yield" value={d.dividend_yield ? `${(d.dividend_yield * 100).toFixed(2)}%` : "—"} accent="teal"/>
              <KpiBox label="52W High" value={d["52w_high"] ? `₹${fmt(d["52w_high"].toFixed?.(2))}` : "—"} accent="emerald"/>
              <KpiBox label="52W Low" value={d["52w_low"] ? `₹${fmt(d["52w_low"].toFixed?.(2))}` : "—"} accent="red"/>
              <KpiBox label="Sector" value={d.sector?.replace("_", " ")} accent="indigo"/>
              <KpiBox label="Revenue (Cr)" value={fmt(d.revenue_cr)} accent="cyan"/>
              <KpiBox label="EBITDA %" value={fmt(d.ebitda_margin)} accent="lime"/>
              <KpiBox label="PAT (Cr)" value={fmt(d.pat_cr)} accent="amber"/>
            </div>

            {d.quarterly_trend?.length > 0 && (
              <div className="bg-white rounded border border-slate-200 p-4">
                <h3 className="font-semibold mb-3 text-slate-900">Quarterly Trend (Revenue vs PAT)</h3>
                <QuarterChart data={d.quarterly_trend}/>
              </div>
            )}

            {d.shareholding && (
              <div>
                <h3 className="font-semibold mb-3 text-slate-900">Shareholding Pattern</h3>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(d.shareholding).map(([k, v]) => (
                    <div key={k} className="rounded p-3 text-center" style={{ background: `linear-gradient(135deg, #f0f9ff, #e0f2fe)` }}>
                      <div className="text-xs text-slate-600 capitalize">{k}</div>
                      <div className="font-mono text-xl text-slate-900 font-bold">{v}%</div>
                      <div className="h-1.5 bg-slate-200 rounded mt-2 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600" style={{ width: `${Math.min(100, v)}%` }}/>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {d.peers?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2 text-slate-900">Peers</h3>
                <div className="flex flex-wrap gap-2">
                  {d.peers.map((p) => <Badge key={p.symbol} className={`${SECTOR_COLORS[d.sector] || ""} cursor-pointer`}>{p.name}</Badge>)}
                </div>
              </div>
            )}

            {d.ai_summary && (
              <div className="bg-gradient-to-br from-violet-50 to-blue-50 border border-violet-200 rounded p-4 text-sm">
                <div className="font-semibold mb-1 text-slate-900">AI Snapshot</div>
                <p className="text-slate-700">{d.ai_summary}</p>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-rose-50 border border-rose-200 rounded p-3 text-sm">
                <div className="font-semibold text-rose-900 mb-1">Key Risks</div>
                <p className="text-slate-700 text-xs">{d.key_risks}</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm">
                <div className="font-semibold text-emerald-900 mb-1">Key Opportunities</div>
                <p className="text-slate-700 text-xs">{d.key_opps}</p>
              </div>
            </div>

            {refs.length > 0 && (
              <div className="bg-amber-50/40 border border-amber-200 rounded p-3" data-testid="company-refs">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="h-4 w-4 text-[#C2A671]"/>
                  <h3 className="font-semibold text-sm">SEBI / Valuation References</h3>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {refs.map((r) => (
                    <a key={r.id} href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs border border-amber-200 rounded p-2 bg-white hover:border-amber-400">
                      <div className="font-medium text-slate-900 line-clamp-1">{r.label}</div>
                      <div className="text-[10px] text-slate-500 inline-flex items-center gap-1">{r.source} <ExternalLink className="h-3 w-3"/></div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function QuarterChart({ data }) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
        <XAxis dataKey="q" tick={{ fontSize: 11 }}/>
        <YAxis tick={{ fontSize: 11 }}/>
        <Tooltip/>
        <Legend wrapperStyle={{ fontSize: 11 }}/>
        <Bar dataKey="revenue" fill="#3b82f6" name="Revenue"/>
        <Bar dataKey="pat" fill="#10b981" name="PAT"/>
      </BarChart>
    </ResponsiveContainer>
  );
}

function KpiBox({ label, value, accent = "slate" }) {
  const colors = {
    emerald: "from-emerald-50 to-emerald-100 border-emerald-200",
    blue: "from-blue-50 to-blue-100 border-blue-200",
    violet: "from-violet-50 to-violet-100 border-violet-200",
    rose: "from-rose-50 to-rose-100 border-rose-200",
    amber: "from-amber-50 to-amber-100 border-amber-200",
    teal: "from-teal-50 to-teal-100 border-teal-200",
    red: "from-red-50 to-red-100 border-red-200",
    indigo: "from-indigo-50 to-indigo-100 border-indigo-200",
    cyan: "from-cyan-50 to-cyan-100 border-cyan-200",
    lime: "from-lime-50 to-lime-100 border-lime-200",
    slate: "from-slate-50 to-slate-100 border-slate-200",
  };
  return (
    <div className={`bg-gradient-to-br ${colors[accent] || colors.slate} border rounded p-3`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-600">{label}</div>
      <div className="text-base font-semibold mt-1 text-slate-900">{value}</div>
    </div>
  );
}
